/** @license DAFT Core v1.2.0
* Copyright 2020 Texas A&M Center for Applied Technology,
* Texas A&M Engineering Experiment Station, The Texas A&M
* University System.
*
* This source code/schema and any derivative work is Texas
* A&M Engineering Experiment Station intellectual property
* and protected by copyright law. Usage is restricted solely
* to the FARM Animal Care and Environmental Stewardship
* programs administered by the National Milk Producers
* Federation. Dissemination of this source code and/or
* derivative work creation is strictly prohibited without
* express written permission from the Texas A&M Engineering
* Experiment Station.
*/

import { containsNullOrUndefined, isNullOrUndefined } from '..';
import { map } from 'rxjs/operators';
import { CeilExpression, CountExpression, DivExpression, FloorExpression, MaxExpression, MinExpression, MultiplyExpression, RoundExpression, StrLenExpression, SubtractExpression, SumExpression } from './expressions';
import { DFSValueType } from './form';
import { ProcessorFunction } from "./processor";

export const $sum: ProcessorFunction<SumExpression, number> = function(r, t, n) {
  return n.watch(r.terms, t)
    .pipe(
      map((t: DFSValueType) => {

        if (!isNumericArray(t)) {
          return null;
        }

        // check for number or isNaN
        var n = t.reduce((r, t) => {
            /** correction factor to avoid js precision issues */
            const cf = 100000000000;
            return ((r * cf) + (t ? (Number(t) * cf) : 0)) / cf;
        }, 0);

        if (isNaN(n)) {
          throw new Error("Invalid summation of terms " + t + " in expression " + JSON.stringify(r));
        }

        return n;
      })
    )
};

export const $sub: ProcessorFunction<SubtractExpression, number> = function(r, t, n) {
  return n.watch(r.terms, t)
    .pipe(
      map((t: DFSValueType) => {

        if (!isNumericArray(t)) {
          console.warn(`$sub requires an array of numbers. Got ${t}`);
          return null;
        }

        var n = t.reduce((r, t, n) => {
            /** correction factor to avoid js precision issues */
            const cf = 100000000000;
            return n === 0 ? Number(t) : (((r * cf) - (Number(t) * cf)) / cf);
        });

        if (isNaN(n)) {
          throw new Error("Invalid terms " + t + " in expression " + JSON.stringify(r));
        }

        return n;
      })
    )
}

export const $multiply: ProcessorFunction<MultiplyExpression, number> = function(r, t, n) {
  return n.watch(r.terms, t)
    .pipe(
      map((t: DFSValueType) => {

        if (!isNumericArray(t)) {
          console.warn(`$multiply requires an array of numbers. Got ${t}`);
          return null;
        }

        if (containsNullOrUndefined(t)) {
          return null;
        }

        var n = t.reduce(function(r, t, n) {
            return n === 0 ? Number(t) : r * Number(t)
        });

        if (isNaN(n)) {
          throw new Error("Invalid terms " + t + " in expression " + JSON.stringify(r));
        }

        return n;
      })
    )
}

export const $div: ProcessorFunction<DivExpression, number | null> = function(r, t, n) {
    return n.watch(r.terms, t).pipe(
      map(function(r: DFSValueType) {
        if (!isNumericArray(r)) {
          console.warn(`$div requires an array of numbers. Got ${r}`);
          return null;
        }

        if (containsNullOrUndefined(r)) {
          return null;
        }

        var t = r.slice(1).reduce(function(prev, next) {
            return prev / next
        }, r[0]);

        if (isNaN(t)) {
          throw new Error("Invalid division of terms' " + r);
        }

        return t;
    }),
    map(function(r) {
        return r === Infinity ? null : r
    }))
};

export const $count: ProcessorFunction<CountExpression, number> = function(r, t, n) {
    return n.watch(r.target, t).pipe(
      map(function(t) {
        if (!t) {
          return 0;
        }

        if (!Array.isArray(t)) {
          throw new Error("$count: target " + r.target + " must be an array");
        }

        return t.length;
    }))
};

export const $ceil: ProcessorFunction<CeilExpression, number> = function(r, t, n) {
    return n.watch(r.target, t).pipe(
      map(function(r: DFSValueType) {
        return isNullOrUndefined(r) ? null : Math.ceil(r as number);
    }))
};

export const $floor: ProcessorFunction<FloorExpression, number> = function(r, t, n) {
    return n.watch(r.target, t).pipe(
      map(function(r: DFSValueType) {
        return isNullOrUndefined(r) ? null : Math.floor(r as number);
    }))
};

export const $round: ProcessorFunction<RoundExpression, number> = function(r, t, n) {
    return n.watch(r.target, t).pipe(
      map(function(n: DFSValueType) {
        if (!r.precision) {
          r.precision = 0;
        }
        return isNullOrUndefined(n) ? null : +(Math.round(+`${n}e+${r.precision}`)  + `e-${r.precision}`);
    }))
};

export const $min: ProcessorFunction<MinExpression, number> = function(r, t, n) {
    return n.watch(r.terms, t).pipe(
      map(function(r: DFSValueType) {
        if (!isNumericArray(r)) {
          console.warn(`$min requires an array of numbers. Got ${r}`);
          return null;
        }
        var t = r.filter(r => !isNullOrUndefined(r));

        if (t.length === 0) {
          return null;
        }

        return Math.min(...t);
    }))
};

export const $max: ProcessorFunction<MaxExpression, number> = function(r, t, n) {
    return n.watch(r.terms, t).pipe(
      map(function(r: DFSValueType) {
        if (!isNumericArray(r)) {
          console.warn(`$max requires an array of numbers. Got ${r}`);
          return null;
        }

        var t = r.filter(r => !isNullOrUndefined(r));

        if (t.length === 0) {
          return null;
        }

        return Math.max(...t);
    }))
};

export const $strlen: ProcessorFunction<StrLenExpression, number> = function(r, t, n) {
    return n.watch(r.target, t)
      .pipe(
        map((r: DFSValueType) => (r as string).length)
      )
};

function isNumericArray(r: DFSValueType): r is number[] {
  return Array.isArray(r) && r.some(r => typeof r === 'number');
}
